import React from "react";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import {
  MultiSelectDropdown,
  type Option as MultiSelectOption,
} from "@/components/MultipleSelectDropdown";
import { cn } from "@/utils/cn";
import { GripVertical } from "lucide-react";
import { Text, Heading } from "@/components/Typography";

export interface AgendaItem {
  id: string;
  description: string;
  duration: number;
}

/**
 * Wrapper for the Briefing Artifact column.
 *
 * On the page it is a Card. In the drawer it is a plain div: Card carries a
 * `.card` CSS rule that sets background, border, padding and shadow as real
 * declarations, and utility classes don't reliably override those — so the
 * card's white background showed through as a patch on the drawer's own panel.
 */
const BriefingShell = ({
  isDrawer,
  children,
}: {
  isDrawer: boolean;
  children: React.ReactNode;
}) =>
  isDrawer ? (
    <div>{children}</div>
  ) : (
    <Card className="bg-white dark:bg-session-card-dark-bg border border-gray-200 dark:border-session-card-dark-border rounded-lg p-6 lg:flex lg:flex-col lg:max-h-[calc(100vh-180px)]">
      {children}
    </Card>
  );

export interface BriefingArtifactProps {
  /** Shown in the Session Overview block. */
  sessionTitle?: string | null;
  /** Everything selectable in the Goals dropdown. */
  goalOptions: MultiSelectOption[];
  selectedGoals: MultiSelectOption[];
  onGoalsChange: (selected: MultiSelectOption[]) => void;

  agendaItems: AgendaItem[];
  onAddAgendaBlock: () => void;
  onUpdateAgendaItem: (id: string, field: "description" | "duration", value: string | number) => void;

  onSaveDraft: () => void;
  onMarkAsReady: () => void;
  isMarkingReady?: boolean;

  /**
   * Read-only audience. Drops both action buttons, swaps the Goals dropdown for
   * plain chips and the agenda inputs for text, and hides "+ Add Block".
   *
   * This is what lets one component serve both sides of a briefing: on a PAB the
   * achiever writes and the advisors read; on an Expert Program the experts
   * write and the cohort reads. The roles invert, the artifact does not.
   */
  readOnly?: boolean;
  /** Copy under the heading when there is nothing to show a reader yet. */
  emptyReadOnlyMessage?: string;
  isDrawer?: boolean;
}

/**
 * Goals + Agenda for one session — the shared body of every session-prep view.
 *
 * Extracted from SessionPrepPage so the Expert Program flow reuses this exact
 * UI instead of growing a near-identical copy. SessionPrep (PAB) and
 * ExpertSessionPrep both render it; they differ only in where the data comes
 * from and who is allowed to write.
 */
export const BriefingArtifact = ({
  sessionTitle,
  goalOptions,
  selectedGoals,
  onGoalsChange,
  agendaItems,
  onAddAgendaBlock,
  onUpdateAgendaItem,
  onSaveDraft,
  onMarkAsReady,
  isMarkingReady = false,
  readOnly = false,
  emptyReadOnlyMessage = "The expert hasn't shared a briefing for this session yet.",
  isDrawer = false,
}: BriefingArtifactProps) => {
  const hasContent = selectedGoals.length > 0 || agendaItems.length > 0;

  return (
    <BriefingShell isDrawer={isDrawer}>
      {/* Header with Action Buttons */}
      <div
        className={cn(
          "flex flex-shrink-0 gap-3",
          isDrawer
            ? "flex-col items-start sm:flex-row sm:items-center sm:justify-between mb-5"
            : "items-center justify-between mb-6",
        )}
      >
        <Heading as="h3" className="text-lg font-semibold text-gray-900 dark:text-white">
          Briefing Artifact
        </Heading>
        {!readOnly && (
          <div className={cn("flex gap-3", isDrawer && "w-full sm:w-auto")}>
            <Button
              variant="outlinePrimary"
              size="md"
              onClick={onSaveDraft}
              className={cn(
                "session-outline-btn px-4 py-2",
                isDrawer ? "h-9 flex-1 text-sm sm:flex-none" : "text-md",
              )}
            >
              Save Draft
            </Button>
            <Button
              variant="primaryGradient"
              size="md"
              onClick={onMarkAsReady}
              className={cn("px-4 py-2 text-sm text-white", isDrawer ? "h-9 flex-1 sm:flex-none" : "")}
            >
              {isMarkingReady ? "Marking as Ready..." : "Mark As Ready"}
            </Button>
          </div>
        )}
      </div>

      {/* In the drawer, the caller's own root (h-full overflow-y-auto) is the
          scroll container — AppDrawer is overflow-hidden at every level — so
          this must not scroll too, or the wheel gets trapped. */}
      <div className={cn("space-y-6", isDrawer ? "" : "overflow-y-auto pr-1 lg:flex-1 lg:min-h-0")}>
        {/* Session Overview */}
        <div>
          <Heading as="h5" className="mb-3 text-sm text-gray-700 dark:text-text-gray">
            Session Overview
          </Heading>
          {sessionTitle && (
            <div className="mb-3 w-full bg-surface-card-inner p-3 rounded-lg border border-surface-border">
              <Text as="p" className="text-base font-medium text-gray-900 dark:text-white">
                {sessionTitle}
              </Text>
            </div>
          )}
        </div>

        {/* Nothing shared yet — only a reader sees this. An editor sees the
            empty dropdown and agenda placeholder, which are the affordances
            for filling it in. */}
        {readOnly && !hasContent && (
          <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center dark:border-surface-border">
            <Text as="p" className="text-sm text-gray-500 dark:text-theme-text-muted">
              {emptyReadOnlyMessage}
            </Text>
          </div>
        )}

        {/* Goals */}
        {(!readOnly || selectedGoals.length > 0) && (
          <div>
            <Heading as="h4" className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
              Goals
            </Heading>
            {readOnly ? (
              <div className="flex flex-wrap gap-2">
                {selectedGoals.map((goal) => (
                  <span
                    key={goal.value}
                    className="rounded-lg border border-surface-border bg-surface-card-inner px-3 py-1.5 text-sm text-gray-900 dark:text-white"
                  >
                    {goal.label}
                  </span>
                ))}
              </div>
            ) : (
              <MultiSelectDropdown
                options={goalOptions}
                value={selectedGoals}
                onChange={onGoalsChange}
                placeholder="Select goals"
                itemLabel="goal"
                className="!bg-surface-card-inner !border-surface-border"
              />
            )}
          </div>
        )}

        {/* Agenda */}
        {(!readOnly || agendaItems.length > 0) && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <Heading as="h4" className="text-base font-semibold text-gray-900 dark:text-white">
                Agenda
              </Heading>
              {!readOnly && (
                <Button variant="ghost" size="sm" onClick={onAddAgendaBlock} className="text-sm">
                  + Add Block
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {!readOnly && agendaItems.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center dark:border-surface-border">
                  <Text as="p" className="text-sm text-gray-500 dark:text-theme-text-muted">
                    No agenda items yet. Use “+ Add Block” to build the agenda.
                  </Text>
                </div>
              )}
              {agendaItems.map((item) => (
                <div
                  key={item.id}
                  /* surface-card-inner in both themes: bg-white made the row
                     invisible against the drawer's own white panel. */
                  className="flex items-center gap-3 p-3 bg-surface-card-inner rounded-lg border border-surface-border"
                >
                  {!readOnly && (
                    <GripVertical className="hidden h-5 w-5 flex-shrink-0 text-gray-400 sm:block" />
                  )}

                  {/* Description takes the remaining space; duration is a
                      fixed-width column so rows line up regardless of how
                      narrow the drawer gets. */}
                  <div className="flex w-full min-w-0 items-center gap-3">
                    {readOnly ? (
                      <>
                        <Text
                          as="p"
                          className="min-w-0 flex-1 text-sm text-gray-900 dark:text-white"
                        >
                          {item.description || "Untitled item"}
                        </Text>
                        {item.duration > 0 && (
                          <Text
                            as="span"
                            className="shrink-0 text-xs text-gray-500 dark:text-theme-text-muted"
                          >
                            {item.duration} min
                          </Text>
                        )}
                      </>
                    ) : (
                      <>
                        <Input
                          id={`agenda-${item.id}-description`}
                          value={item.description}
                          onChange={(e) => onUpdateAgendaItem(item.id, "description", e.target.value)}
                          placeholder="Enter agenda item"
                          className="h-[46px] w-full min-w-0 flex-1 bg-surface-card dark:bg-surface-card-inner"
                        />
                        <div className="flex shrink-0 items-center gap-1.5">
                          <Input
                            id={`agenda-${item.id}-duration`}
                            type="number"
                            value={item.duration}
                            onChange={(e) =>
                              onUpdateAgendaItem(item.id, "duration", parseInt(e.target.value))
                            }
                            placeholder="0"
                            className="h-[46px] w-[72px] bg-surface-card dark:bg-surface-card-inner"
                            min="0"
                          />
                          <Text as="span" className="text-xs text-gray-500 dark:text-theme-text-muted">
                            min
                          </Text>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BriefingShell>
  );
};

export default BriefingArtifact;
